import { hydrate, render } from "preact";
import { PageShell } from "./PageShell";
import "../sentry.vite.config";
import type { PageContext } from "vike/types";
import { getLocaleFromPathname } from "./getLocaleFromPathname";
import { getTranslation } from "@/shared/seo/getTranslation";
import { getLocaleFromCookie } from "@/shared/utils/locale-cookies";

export default onRenderClient;

type ExtendedPageContext = PageContext & {
  Page: React.ComponentType<Record<string, unknown>>;
  pageProps: Record<string, unknown>;
  isHydration: boolean;
  documentProps?: {
    title?: string;
    description?: string;
    image?: string;
    url?: string;
    noindex?: boolean;
  };
};

async function onRenderClient(pageContext: ExtendedPageContext): Promise<void> {
  const { Page, pageProps, isClientSideNavigation } = pageContext;
  const page = (
    <PageShell pageContext={pageContext}>
      <Page {...pageProps} />
    </PageShell>
  );
  const container = document.querySelector("body");

  if (!container) {
    throw new Error("Container not found");
  }

  if (pageContext.isHydration) {
    hydrate(page, container);
  } else {
    render(page, container);
  }

  const isLangInPathname = /^\/[a-z]{2}(\/.*)?$/.test(
    pageContext.urlPathname || "",
  );

  if (!isLangInPathname) {
    const cookieLocale = getLocaleFromCookie();
    if (cookieLocale) {
      document.documentElement.lang = cookieLocale;
    }
  }

  if (!isClientSideNavigation && !isLangInPathname) {
    document.title = getPageTitle(pageContext);
  }
}

function getPageTitle(pageContext: ExtendedPageContext): string {
  const { documentProps, urlPathname } = pageContext;

  if (documentProps?.title) {
    return documentProps.title;
  }

  const cookieLocale = getLocaleFromCookie();
  if (cookieLocale) {
    return getTranslation(cookieLocale, "seo:default_title");
  }

  const locale = getLocaleFromPathname(urlPathname || "/");
  return getTranslation(locale, "seo:default_title");
}
